import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { TABLES } from '../../lib/constants';
import { useAuth } from '../../context/AuthContext';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

const { 
  FiAlertOctagon, FiRefreshCw, FiShield, FiTrash2, 
  FiInfo, FiLock, FiMessageSquare, FiSettings, FiActivity 
} = FiIcons;

const AdminSettings = () => {
  const { user } = useAuth();
  const [isConfirming, setIsConfirming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetKey, setResetKey] = useState('');

  const handleRagnarok = async () => {
    if (resetKey !== 'RAGNAROK') return;
    setLoading(true);
    try {
      // Destructive cleanup of active data tables
      await supabase.from(TABLES.LOGS).delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from(TABLES.CHARACTERS).delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from(TABLES.ITEMS).delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from(TABLES.USERS).delete().neq('role', 'super_admin');
      
      alert('Ragnarok Completed. All non-admin data has been purged.');
      setIsConfirming(false);
      setResetKey('');
    } catch (err) {
      alert('Reset failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-12 pb-20 max-w-5xl mx-auto">
      <header>
        <h2 className="text-5xl font-black text-gray-900 tracking-tight">System Logs & Config</h2>
        <p className="text-gray-400 font-bold text-lg mt-2 italic">Global moderation and emergency protocols.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Community Moderation Info */}
        <div className="space-y-8">
          <div className="bento-card border-l-4 border-l-blue-500 h-full">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center shadow-sm">
                <FiMessageSquare className="text-xl" />
              </div>
              <div>
                <h3 className="text-xl font-black text-gray-900">Community Moderation</h3>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Public Feed Engagement</p>
              </div>
            </div>
            <div className="space-y-4">
              <p className="text-sm font-medium text-gray-600 leading-relaxed">
                Global commenting is currently enabled for all <span className="text-blue-600 font-black text-xs uppercase">Logged-in Users</span>. 
              </p>
              <div className="p-4 bg-blue-50/50 rounded-2xl border border-blue-100/50">
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-[10px] font-black text-blue-700 uppercase tracking-tight">
                    <FiLock className="shrink-0" /> Comments are persistent and public
                  </li>
                  <li className="flex items-center gap-3 text-[10px] font-black text-blue-700 uppercase tracking-tight">
                    <FiActivity className="shrink-0" /> Real-time activity sync enabled
                  </li>
                </ul>
              </div>
              <p className="text-[10px] font-bold text-gray-400 italic mt-4">
                * Admin moderation dashboard for granular comment control is scheduled for the next system update.
              </p>
            </div>
          </div>
        </div>

        {/* EMERGENCY RESET SECTION */}
        <div className="space-y-8">
          <div className="bg-white rounded-[3rem] p-10 border-2 border-red-50 shadow-2xl shadow-red-100/50 relative overflow-hidden h-full">
            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-8 text-red-600">
                <div className="w-12 h-12 bg-red-600 text-white rounded-2xl flex items-center justify-center shadow-xl">
                  <FiAlertOctagon className="text-2xl" />
                </div>
                <div>
                  <h3 className="text-2xl font-black tracking-tight italic">Ragnarok</h3>
                  <p className="text-[10px] font-black text-red-400 uppercase tracking-widest">Master Reset Protocol</p>
                </div>
              </div>

              <p className="text-xs font-bold text-gray-500 mb-8 leading-relaxed">
                Executing this protocol will permanently delete all Drop Logs, Characters, Custom Items, and Pilot accounts. <b>This action cannot be undone.</b>
              </p>

              {!isConfirming ? (
                <button 
                  onClick={() => setIsConfirming(true)} 
                  className="w-full py-6 bg-gray-900 text-white rounded-[2rem] font-black text-sm hover:bg-black transition-all active:scale-95 shadow-2xl flex items-center justify-center gap-4 group"
                >
                  Initialize Purge <FiRefreshCw className="group-hover:rotate-180 transition-transform duration-700" />
                </button>
              ) : (
                <div className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-4">Security Confirmation</label>
                    <input 
                      type="text" 
                      value={resetKey} 
                      onChange={(e) => setResetKey(e.target.value.toUpperCase())} 
                      className="apple-input !bg-red-50 !text-red-600 !py-6 text-center text-xl font-black placeholder:text-red-200" 
                      placeholder="TYPE RAGNAROK" 
                    />
                  </div>
                  <div className="flex flex-col gap-3">
                    <button 
                      onClick={handleRagnarok} 
                      disabled={loading || resetKey !== 'RAGNAROK'} 
                      className="w-full py-5 bg-red-600 text-white rounded-[1.5rem] font-black uppercase text-[10px] tracking-widest shadow-xl shadow-red-200 disabled:opacity-50 disabled:shadow-none"
                    >
                      {loading ? 'Purging Database...' : 'Finalize Execution'}
                    </button>
                    <button 
                      onClick={() => {
                        setIsConfirming(false);
                        setResetKey('');
                      }} 
                      className="w-full py-5 bg-gray-100 text-gray-500 rounded-[1.5rem] font-black uppercase text-[10px] hover:bg-gray-200 transition-all"
                    >
                      Abort Protocol
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Background Decorative Element */}
            <div className="absolute -bottom-10 -right-10 opacity-[0.03] pointer-events-none">
              <FiTrash2 size={200} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;